// Comercial › Licitações › Pipeline — o funil próprio das licitações (02/10/2026).
//
// Pedido do Lucas: separar o funil de licitações do de propostas privadas,
// porque conduzir um edital público é outro trabalho: prazos do órgão,
// alterações, habilitação por documento, sessão, recursos, assinatura.
// Desenho e decisões: docs/licitacoes-pipeline.md.
//
// Dois andares acima do quadro, como no pipeline privado (regra do Lucas de
// 01/10: "cada número aparece uma vez por tela"): a barra de filtros e UMA
// linha de resumo com os atalhos de cobrança. O funil é o cabeçalho das
// colunas. Vistas: Kanban · Lista · Agenda (todos os prazos num calendário).
//
// A tela da licitação (TelaLicitacao) abre AQUI DENTRO, como o NegocioDetalhe
// abre dentro do Pipeline — e por `?licitacao=<id>` (sino, e-mail de prazo) ou
// pela chave `licitacoes_abrir_oportunidade_id` que o App grava ao clicar no sino.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Flex, Input, Radio, Segmented, Select, Tag, Tooltip, Typography, App as AntApp } from 'antd';
import {
  AppstoreOutlined, UnorderedListOutlined, CalendarOutlined, SearchOutlined,
  WarningOutlined, ClockCircleOutlined, UserOutlined, ExclamationCircleOutlined, PauseCircleOutlined,
} from '@ant-design/icons';
import { usePerms } from '../../../hooks/usePerms';
import { licApi, erroDe, type Vendedor } from './api';
import { ehAberta, pedeMotivo, rotuloDe } from './etapas';
import {
  casaSinal, resumoDoQuadro, type LicitacaoPipeline, type Ordenacao, type Sinal,
} from './saudeLicitacao';
import { valorCompacto } from '../../comercial-pipeline/saudeNegocio';
import { fmt } from '../../comercial-pipeline/utils';
import { KanbanLicitacoes } from './KanbanLicitacoes';
import { ListaLicitacoes } from './ListaLicitacoes';
import { AgendaLicitacoes } from './AgendaLicitacoes';
import { tituloDoObjeto, nomeDaLicitacao } from '../tituloDoObjeto';
import { ModalFecharLicitacao, type TipoFechamento, type DadosFechamento } from './ModalFecharLicitacao';
import TelaLicitacao from '../TelaLicitacao';

const { Text } = Typography;

type Vista = 'kanban' | 'lista' | 'agenda';

function lerSessao<T extends string>(chave: string, validos: readonly T[], padrao: T): T {
  try {
    const v = sessionStorage.getItem(chave) as T | null;
    return v && validos.includes(v) ? v : padrao;
  } catch { return padrao; }
}

function PipelineLicitacoesInner() {
  const { message } = AntApp.useApp();
  const { pode } = usePerms();
  const podeOperar = pode('comercial.licitacoes.pipeline.operar');
  const [sp, setSp] = useSearchParams();

  const [lista, setLista] = useState<LicitacaoPipeline[]>([]);
  const [loading, setLoading] = useState(true);
  const [vendedores, setVendedores] = useState<Vendedor[]>([]);
  const [vista, setVistaEstado] = useState<Vista>(() => lerSessao('lic_pipeline_vista', ['kanban', 'lista', 'agenda'] as const, 'kanban'));
  const setVista = (v: Vista) => { setVistaEstado(v); try { sessionStorage.setItem('lic_pipeline_vista', v); } catch { /* sem storage */ } };
  const [ordenacao, setOrdenacaoEstado] = useState<Ordenacao>(() => lerSessao('lic_pipeline_ordenacao', ['prazo', 'valor', 'recentes'] as const, 'prazo'));
  const setOrdenacao = (o: Ordenacao) => { setOrdenacaoEstado(o); try { sessionStorage.setItem('lic_pipeline_ordenacao', o); } catch { /* sem storage */ } };
  const [busca, setBusca] = useState('');
  const [filtroUf, setFiltroUf] = useState('');
  const [filtroModalidade, setFiltroModalidade] = useState('');
  const [filtroResponsavel, setFiltroResponsavel] = useState('');
  const [filtroEtapa, setFiltroEtapa] = useState('');
  const [sinal, setSinal] = useState<Sinal>('');
  const [aberta, setAberta] = useState<number | null>(null);
  const [fechando, setFechando] = useState<{ l: LicitacaoPipeline; tipo: TipoFechamento } | null>(null);

  const carregar = useCallback(async (mostrarLoading = true) => {
    if (mostrarLoading) setLoading(true);
    try {
      const dados = await licApi.pipeline();
      setLista(Array.isArray(dados) ? dados : []);
    } catch (e: any) {
      message.error('Não consegui ler o pipeline de licitações: ' + erroDe(e));
    } finally { if (mostrarLoading) setLoading(false); }
  }, [message]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    licApi.vendedores().then(setVendedores).catch(() => { /* o avatar mostra o e-mail */ });
  }, []);

  // Deep-link: `?licitacao=<id>` (e-mail de prazo, link copiado) e a chave do sino.
  useEffect(() => {
    const daUrl = sp.get('licitacao');
    let doSino: string | null = null;
    try { doSino = sessionStorage.getItem('licitacoes_abrir_oportunidade_id'); } catch { /* sem storage */ }
    const id = parseInt(daUrl || doSino || '', 10);
    if (id) {
      setAberta(id);
      try { sessionStorage.removeItem('licitacoes_abrir_oportunidade_id'); } catch { /* sem storage */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp.get('licitacao')]);

  const nomes = useMemo(() => {
    const m: Record<string, string> = {};
    for (const v of vendedores) if (v?.email) m[String(v.email).toLowerCase()] = v.nome || v.email;
    return m;
  }, [vendedores]);

  const substituir = (nova: LicitacaoPipeline) =>
    setLista(prev => prev.some(x => x.id === nova.id) ? prev.map(x => (x.id === nova.id ? nova : x)) : [nova, ...prev]);

  async function mover(l: LicitacaoPipeline, para: string, extra: Record<string, any> = {}) {
    try {
      const nova = await licApi.mover(l.id, para, extra);
      substituir(nova);
      message.success(`${tituloDoObjeto(l.objeto, 60) || l.numero_edital || 'Licitação'} → ${rotuloDe(para)}`);
      return true;
    } catch (e: any) {
      message.error(erroDe(e));
      return false;
    }
  }

  // Arrastar, trocar na lista ou clicar na trilha: destino com motivo ou valor
  // abre a pergunta antes; o resto vai direto (quem recusa é o servidor).
  function pedirMovimento(l: LicitacaoPipeline, para: string) {
    if (pedeMotivo(para) || para === 'ganho') { setFechando({ l, tipo: para as TipoFechamento }); return; }
    mover(l, para);
  }

  async function confirmarFechamento(dados: DadosFechamento) {
    if (!fechando) return;
    const okMovido = await mover(fechando.l, fechando.tipo, dados);
    if (okMovido) setFechando(null);
  }

  // ── Filtros e resumo ─────────────────────────────────────────────────────
  const ufs = useMemo(() => Array.from(new Set(lista.map(l => l.uf).filter(Boolean))).sort() as string[], [lista]);
  const modalidades = useMemo(() => Array.from(new Set(lista.map(l => l.modalidade).filter(Boolean))).sort() as string[], [lista]);
  const responsaveis = useMemo(() => {
    const emails = new Set<string>(lista.map(l => String(l.responsavel_email || '').toLowerCase()).filter(Boolean));
    for (const v of vendedores) if (v?.email) emails.add(String(v.email).toLowerCase());
    return Array.from(emails).sort().map(e => ({ value: e, label: nomes[e] ? `${nomes[e]} (${e})` : e }));
  }, [lista, vendedores, nomes]);

  function passaFiltros(l: LicitacaoPipeline, ignorarEtapa = false): boolean {
    if (!ignorarEtapa && filtroEtapa && l.status_interno !== filtroEtapa) return false;
    if (filtroUf && l.uf !== filtroUf) return false;
    if (filtroModalidade && l.modalidade !== filtroModalidade) return false;
    if (filtroResponsavel && String(l.responsavel_email || '').toLowerCase() !== filtroResponsavel) return false;
    if (!casaSinal(l, sinal)) return false;
    if (busca) {
      const q = busca.toLowerCase();
      const alvo = [l.numero_edital, l.numero_processo, l.orgao, l.cidade, l.objeto, l.responsavel_email].map(x => String(x || '').toLowerCase());
      if (!alvo.some(t => t.includes(q))) return false;
    }
    return true;
  }
  const filtradas = lista.filter(l => passaFiltros(l));
  const resumo = resumoDoQuadro(lista.filter(l => {
    // Os atalhos contam dentro dos outros filtros, mas ignoram o próprio atalho.
    if (filtroEtapa && l.status_interno !== filtroEtapa) return false;
    if (filtroUf && l.uf !== filtroUf) return false;
    if (filtroModalidade && l.modalidade !== filtroModalidade) return false;
    if (filtroResponsavel && String(l.responsavel_email || '').toLowerCase() !== filtroResponsavel) return false;
    return true;
  }));
  const temFiltro = !!(busca || filtroUf || filtroModalidade || filtroResponsavel || filtroEtapa || sinal);
  const limpar = () => { setBusca(''); setFiltroUf(''); setFiltroModalidade(''); setFiltroResponsavel(''); setFiltroEtapa(''); setSinal(''); };

  if (aberta != null) {
    return (
      <TelaLicitacao oportunidadeId={aberta}
        onVoltar={() => {
          setAberta(null);
          if (sp.get('licitacao')) { const n = new URLSearchParams(sp); n.delete('licitacao'); setSp(n, { replace: true }); }
          carregar(false);
        }}
        onMudou={(nova) => { if (nova) substituir(nova); else carregar(false); }} />
    );
  }

  const chip = (chave: Exclude<Sinal, ''>, n: number, texto: string, icone: React.ReactNode, cor: string) => (
    n > 0 || sinal === chave ? (
      <Tag.CheckableTag key={chave} checked={sinal === chave} onChange={() => setSinal(sinal === chave ? '' : chave)}
        style={{ margin: 0, border: `1px solid ${sinal === chave ? 'transparent' : '#E3E6EE'}`, fontSize: 12 }}>
        <span style={{ color: sinal === chave ? undefined : cor, marginRight: 5 }}>{icone}</span>
        <span style={{ fontWeight: 600 }}>{n}</span> {texto}
      </Tag.CheckableTag>
    ) : null
  );

  return (
    <div style={{ width: '100%', margin: '12px 0', padding: '0 24px', boxSizing: 'border-box' }}>
      {/* Tela estreita: a linha de resumo rola de lado em vez de empilhar seis linhas. */}
      <style>{`@media (max-width: 768px) { .lp-resumo { flex-wrap: nowrap !important; overflow-x: auto; white-space: nowrap; padding-bottom: 2px; } .lp-resumo > * { flex: none; } }`}</style>
      <Flex gap={10} align="center" wrap="wrap" style={{ marginBottom: 10 }}>
        <Radio.Group value={vista} onChange={(e) => setVista(e.target.value)} size="small">
          <Radio.Button value="kanban"><AppstoreOutlined /> Kanban</Radio.Button>
          <Radio.Button value="lista"><UnorderedListOutlined /> Lista</Radio.Button>
          <Radio.Button value="agenda"><CalendarOutlined /> Agenda</Radio.Button>
        </Radio.Group>
        <Select value={filtroEtapa || undefined} onChange={(v) => setFiltroEtapa(v || '')} placeholder="Todas as etapas"
          allowClear showSearch optionFilterProp="label" size="small" style={{ width: 170 }}
          options={Array.from(new Set(lista.map(l => l.status_interno))).map(c => ({ value: c, label: rotuloDe(c) }))} />
        <Select value={filtroUf || undefined} onChange={(v) => setFiltroUf(v || '')} placeholder="UF"
          allowClear showSearch optionFilterProp="label" size="small" style={{ width: 84 }}
          options={ufs.map(u => ({ value: u, label: u }))} />
        <Select value={filtroModalidade || undefined} onChange={(v) => setFiltroModalidade(v || '')} placeholder="Modalidade"
          allowClear showSearch optionFilterProp="label" size="small" style={{ width: 190 }} popupMatchSelectWidth={false}
          options={modalidades.map(m => ({ value: m, label: m }))} />
        <Select value={filtroResponsavel || undefined} onChange={(v) => setFiltroResponsavel(v || '')} placeholder="Responsável"
          allowClear showSearch optionFilterProp="label" size="small" style={{ width: 190 }} popupMatchSelectWidth={false}
          options={responsaveis} />
        <Input placeholder="Buscar edital, órgão, objeto..." value={busca} onChange={e => setBusca(e.target.value)}
          prefix={<SearchOutlined style={{ color: '#aaa' }} />} size="small" allowClear style={{ flex: 1, minWidth: 150, maxWidth: 340 }} />
        {temFiltro && (
          <>
            <Text type="secondary" style={{ fontSize: 11 }}>{filtradas.length}/{lista.length}</Text>
            <Button size="small" type="link" style={{ padding: 0, height: 'auto' }} onClick={limpar}>Limpar filtros</Button>
          </>
        )}
        <Tooltip title="Editais novos entram pela triagem (aba Oportunidades) e chegam aqui pelo botão Avaliar">
          <Button size="small" style={{ marginLeft: 'auto' }} href="/comercial/licitacoes?aba=oportunidades">Oportunidades da triagem</Button>
        </Tooltip>
      </Flex>

      {!loading && lista.length > 0 && vista !== 'agenda' && (
        <div className="lp-resumo" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 10px', alignItems: 'center', marginBottom: 10, minHeight: 24, fontSize: 12, color: '#5A6378' }}>
          <Tooltip title={`Em disputa (etapas abertas, dentro dos filtros): ${fmt(resumo.abertas.valor)} estimados`}>
            <span><b style={{ color: '#0E1433' }}>{resumo.abertas.qtd}</b> em disputa · <b style={{ color: '#0E1433' }}>{valorCompacto(resumo.abertas.valor)}</b> estimados</span>
          </Tooltip>
          <span style={{ width: 1, height: 14, background: '#E3E6EE' }} />
          {chip('vencendo', resumo.sinais.vencendo, 'com prazo em até 7 dias', <ClockCircleOutlined />, '#B07500')}
          {chip('vencido', resumo.sinais.vencido, 'com prazo vencido', <ExclamationCircleOutlined />, '#B91626')}
          {chip('alteracoes', resumo.sinais.alteracoes, 'com alteração não vista', <WarningOutlined />, '#B91626')}
          {chip('sem_responsavel', resumo.sinais.sem_responsavel, 'sem responsável', <UserOutlined />, '#5A6378')}
          {/* Suspensas pelo órgão em que seguimos interessados (20/10/2026): continuam na etapa delas. */}
          {chip('suspensa', resumo.sinais.suspensa, 'suspensas pelo órgão', <PauseCircleOutlined />, '#B07500')}
          {vista === 'kanban' && (
            <span style={{ marginLeft: 'auto' }}>
              <Tooltip title="Ordem dos cartões dentro de cada coluna">
                <Segmented size="small" value={ordenacao} onChange={(v) => setOrdenacao(v as Ordenacao)}
                  options={[{ value: 'prazo', label: 'Prazo' }, { value: 'valor', label: 'Maior valor' }, { value: 'recentes', label: 'Recentes' }]} />
              </Tooltip>
            </span>
          )}
        </div>
      )}

      {vista === 'agenda' ? (
        <AgendaLicitacoes lista={filtradas} onAbrir={(l) => setAberta(l.id)} />
      ) : vista === 'lista' ? (
        <ListaLicitacoes lista={filtradas} loading={loading} nomes={nomes} podeOperar={podeOperar}
          onAbrir={(l) => setAberta(l.id)} onMover={pedirMovimento} />
      ) : (
        <KanbanLicitacoes lista={filtradas} loading={loading} ordenacao={ordenacao} nomes={nomes} podeOperar={podeOperar}
          onAbrir={(l) => setAberta(l.id)} onMover={pedirMovimento} />
      )}

      {!loading && lista.length === 0 && (
        <Text type="secondary" style={{ display: 'block', marginTop: 16, fontSize: 12 }}>
          Nenhuma licitação no pipeline ainda. Na aba <b>Oportunidades</b>, clique em <b>Avaliar</b> no edital que vale olhar — ele entra aqui em "Avaliando", com responsável.
          {ehAberta('avaliando') ? '' : ''}
        </Text>
      )}

      {fechando && (
        <ModalFecharLicitacao tipo={fechando.tipo}
          titulo={nomeDaLicitacao(fechando.l)}
          valorEstimado={fechando.l.valor_estimado != null ? Number(fechando.l.valor_estimado) : null}
          onCancelar={() => setFechando(null)} onConfirmar={confirmarFechamento} />
      )}
    </div>
  );
}

export default function PipelineLicitacoes() {
  return (
    <AntApp>
      <PipelineLicitacoesInner />
    </AntApp>
  );
}
